import React from 'react';
import { Helmet } from 'react-helmet-async';
import QuizShell from '../../components/quiz/QuizShell';
import type { QuizConfig } from '../../components/quiz/QuizShell';

const config: QuizConfig = {
  niche: 'medical-anxiety',
  quizSlug: 'medical-anxiety',
  pageTitle: 'Is fear of medical procedures stopping you from getting the care you need?',
  pageSubtitle: 'Answer 4 quick questions to understand your medical anxiety and how hypnotherapy can help.',
  questions: [
    {
      id: 'procedure-type',
      question: 'Which type of medical situation causes you the most anxiety?',
      options: [
        { label: 'Blood tests or injections', points: 1 },
        { label: 'Scans or enclosed spaces (MRI, CT)', points: 2 },
        { label: 'Surgical procedures or dental work', points: 3 },
      ],
    },
    {
      id: 'anticipatory-anxiety',
      question: 'How do you typically feel in the lead-up to a medical appointment?',
      options: [
        { label: 'Nervous but I can manage it', points: 1 },
        { label: 'Quite anxious — it affects my sleep or mood', points: 2 },
        { label: 'Extremely anxious — I dread it for days beforehand', points: 3 },
      ],
    },
    {
      id: 'avoidance',
      question: 'Have you ever avoided or delayed a medical appointment because of anxiety?',
      options: [
        { label: 'No, I always attend', points: 1 },
        { label: 'Yes, I\'ve put things off a few times', points: 2 },
        { label: 'Yes, I\'ve avoided appointments I know I needed', points: 3 },
      ],
    },
    {
      id: 'daily-impact',
      question: 'How much does medical anxiety affect your everyday life (outside of appointments)?',
      options: [
        { label: 'Mostly just when appointments are coming up', points: 1 },
        { label: 'I often worry about future appointments', points: 2 },
        { label: 'It affects me regularly, even without an appointment scheduled', points: 3 },
      ],
    },
  ],
  tiers: [
    {
      name: 'Mild',
      tierNumber: 1,
      minScore: 4,
      blurb:
        'Your results suggest mild procedure-related anxiety. You manage to attend appointments, but the worry around them affects your wellbeing. Hypnotherapy can help you approach medical care with calm focus rather than dread.',
    },
    {
      name: 'Moderate',
      tierNumber: 2,
      minScore: 7,
      blurb:
        'Your results indicate moderate medical anxiety that is starting to affect how you manage your health. Avoidance at this level is common — and it tends to compound over time. Hypnotherapy can reset the anxiety response so you can get the care you deserve.',
    },
    {
      name: 'Significant',
      tierNumber: 3,
      minScore: 10,
      blurb:
        'Your results show significant medical anxiety that is likely causing you to delay or avoid important treatment. You are not alone in this — it is one of the most common reasons people seek hypnotherapy. Relief is possible, and it can happen more quickly than you think.',
    },
  ],
};

const MedicalAnxietyQuiz: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Medical Anxiety Quiz | Sharon Clare Hypnotherapy</title>
        <meta
          name="description"
          content="Take our short quiz to understand your medical procedure anxiety and find out how hypnotherapy can help."
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/quiz/medical-anxiety/" />
      </Helmet>
      <QuizShell config={config} />
    </>
  );
};

export default MedicalAnxietyQuiz;
